'use client';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { CompanyBanner } from '@/services/dataTypes';
import { getBaseImageUrl } from '@/constants/hooks';

interface Props {
  /** Array of banner groups — each group is one "slide" of 3 banners */
  bannerGroups: CompanyBanner[][];
  domain: string;
}

const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

/** Renders one group of banners in the 3-column layout:
 *  [small] [large] [small]  — matching the original triple banner logic */
const BannerGroup = ({ banners, domain }: { banners: CompanyBanner[]; domain: string }) => (
  <div className="grid grid-cols-12 gap-3 w-full">
    {banners.map((item, i) => {
      const isLarge = i === 1;
      return (
        <div
          key={i}
          className={`${isLarge ? 'col-span-6' : 'col-span-3'} flex flex-col gap-2`}
        >
          {/* Banner image */}
          <Link
            href={item.url || '#'}
            target="_blank"
            rel={isLarge ? undefined : 'nofollow sponsored noopener noreferrer'}
            className="block overflow-hidden rounded-xl group/card transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(71,150,105,0.18)]"
            style={{ border: '1px solid rgba(71,150,105,0.15)', background: '#ffffff' }}
          >
            <Image
              src={getBaseImageUrl(domain, item.image, '')}
              alt={item.text || 'Banner'}
              width={isLarge ? 552 : 270}
              height={isLarge ? 347 : 340}
              className="w-full h-auto object-cover transition-transform duration-500 group-hover/card:scale-[1.03]"
              style={{ borderRadius: '0.75rem' }}
            />
          </Link>

          {/* Banner text pill */}
          {item.text && (
            <Link
              href={item.url || '#'}
              target="_blank"
              rel="nofollow sponsored noopener noreferrer"
              className="block text-center px-3 py-2.5 rounded-xl font-bold text-sm transition-all duration-200 no-underline hover:text-[#479669] hover:border-[#479669] truncate"
              style={{
                background: '#f0faf5',
                border: '1px solid rgba(71,150,105,0.2)',
                color: '#2d6b4a',
              }}
            >
              {item.text}
            </Link>
          )}
        </div>
      );
    })}
  </div>
);

/** Mobile: flat list of all banners as a single-column slider */
const MobileBannerSlider = ({ banners, domain }: { banners: CompanyBanner[]; domain: string }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView: 1.15, spacing: 12 },
    slideChanged(s) { setCurrentSlide(s.track.details.rel); },
  });

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => slider.current?.next(), 4000);
    return () => clearInterval(t);
  }, [slider, banners.length]);

  return (
    <div className="relative">
      <div ref={sliderRef} className="keen-slider">
        {banners.map((item, i) => (
          <div key={i} className="keen-slider__slide">
            <Link
              href={item.url || '#'}
              target="_blank"
              rel="nofollow sponsored noopener noreferrer"
              className="block overflow-hidden rounded-xl border border-gray-100"
            >
              <Image
                src={getBaseImageUrl(domain, item.image, '')}
                alt={item.text || 'Banner'}
                width={400}
                height={250}
                className="w-full h-auto object-cover rounded-xl"
              />
            </Link>
            {item.text && (
              <p
                className="mt-2 text-center text-sm font-bold truncate px-2"
                style={{ color: '#2d6b4a' }}
              >
                {item.text}
              </p>
            )}
          </div>
        ))}
      </div>
      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-3">
        {banners.map((_, i) => (
          <button
            key={i}
            onClick={() => slider.current?.moveToIdx(i)}
            aria-label={`Go to banner ${i + 1}`}
            className="rounded-full transition-all duration-300"
            style={{
              width: i === currentSlide ? '20px' : '6px',
              height: '6px',
              background: i === currentSlide ? '#479669' : 'rgba(71,150,105,0.25)',
            }}
          />
        ))}
      </div>
    </div>
  );
};

const TripleBannerGrid = ({ bannerGroups, domain }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView: 1 },
    slideChanged(s) { setCurrentSlide(s.track.details.rel); },
  });

  const startAutoplay = () => {
    if (timerRef.current || bannerGroups.length <= 1) return;
    timerRef.current = setInterval(() => slider.current?.next(), 4500);
  };

  const stopAutoplay = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [slider, bannerGroups.length]);

  const allBanners = bannerGroups.flat();

  return (
    <>
      {/* ── Desktop: grouped carousel ── */}
      <div
        className="hidden md:block relative group"
        onMouseEnter={stopAutoplay}
        onMouseLeave={startAutoplay}
      >
        <div ref={sliderRef} className="keen-slider">
          {bannerGroups.map((group, i) => (
            <div key={i} className="keen-slider__slide">
              <BannerGroup banners={group} domain={domain} />
            </div>
          ))}
        </div>

        {/* Arrows */}
        {bannerGroups.length > 1 && (
          <>
            <button
              onClick={() => slider.current?.prev()}
              aria-label="Previous"
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
              style={{ background: '#ffffff', border: '1px solid rgba(71,150,105,0.3)', color: '#479669', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ChevronLeft />
            </button>
            <button
              onClick={() => slider.current?.next()}
              aria-label="Next"
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
              style={{ background: '#ffffff', border: '1px solid rgba(71,150,105,0.3)', color: '#479669', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
            >
              <ChevronRight />
            </button>
          </>
        )}

        {/* Dots */}
        {bannerGroups.length > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {bannerGroups.map((_, i) => (
              <button
                key={i}
                onClick={() => slider.current?.moveToIdx(i)}
                aria-label={`Go to group ${i + 1}`}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === currentSlide ? '24px' : '8px',
                  height: '8px',
                  background: i === currentSlide ? '#479669' : 'rgba(71,150,105,0.2)',
                  boxShadow: i === currentSlide ? '0 0 8px rgba(71,150,105,0.4)' : 'none',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Mobile: flat single-column slider ── */}
      <div className="md:hidden">
        <MobileBannerSlider banners={allBanners} domain={domain} />
      </div>
    </>
  );
};

export default TripleBannerGrid;
